import "bootstrap/dist/css/bootstrap.min.css";
import * as d3 from "d3";
import React, { useEffect, useRef, useCallback } from "react";
import { useWebSocket } from "utils/hooks";

const RealtimeDotsGraph = ({
  width,
  height,
  interferences,
  initialDataset,
  defaultOptiopns,
  lookback,
  freeze,
}) => {
  const ref = useRef(null);
  const dataRef = useRef([]);
  const graphVarsRef = useRef([]);

  const scale = 0.5;
  const lookback_s = lookback ? lookback : 30;
  const room_for_axis = 50;
  const room_for_title = defaultOptiopns?.title ? 20 : 0;
  const room_for_legend = defaultOptiopns?.legendValues?.length > 0 ? 25 : 0;
  const updateIntervalMs = 200;

  const updateTimer = useRef();

  const interferenceValuesRef = useRef([]);

  useEffect(() => {
    const yPoints = interferences
      ?.flat()
      .filter((event) => Object.keys(event).length > 0)
      .map((i) => i?.interferenceFrequency&&i?.interferenceFrequency);

      
    if (yPoints?.length > 0) interferenceValuesRef.current = yPoints;  else interferenceValuesRef.current=[]

    // Add interference events
      interferenceValuesRef.current.forEach((val) => {
        val && dataRef.current.push([new Date(), val]);
      });
      interferenceValuesRef.current = [];

  }, [interferences]);

  /** Initialize graph once */
  useEffect(() => {
    if (!ref.current) return;

    const svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height + room_for_title);

    const thresholds = svg.append("g").attr("id", "thresholds");
    const dotsGroup = svg
      .append("g")
      .attr("transform", `translate(-2.5,${room_for_title})`)
      .attr("id", "dots");
    const yScale = d3
      .scaleLinear()
      .domain([defaultOptiopns.yMinValue, defaultOptiopns.yMaxValue])
      .range([height - room_for_axis - room_for_legend, 10]);
    const xScaleGroup = svg.append("g");
    const yScaleGroup = svg.append("g");
    const xGridsGroup = svg
      .append("g")
      .attr("id", "xGridsGroup")
      .attr("opacity", "0.3");
    const yGridsGroup = svg
      .append("g")
      .attr("id", "yGridsGroup")
      .attr("opacity", "0.3");
    const legend = svg.append("g").attr("id", "legends");

    const title = svg
      .append("text")
      .attr("transform", "translate(" + width / 2 + ",0)")
      .attr("y", 15)
      .attr("font-size", "15px")
      .attr("text-anchor", "middle")
      .attr("stroke", "white")
      .attr("letter-spacing", "0.054em")
      .text(defaultOptiopns.title);

    if (defaultOptiopns.xLabelName) {
      const xAxisLabel = svg
        .append("g")
        .attr("id", "xAxisLabel")
        .attr("transform", "translate(" + 35 + "," + 35 + ")");

      xAxisLabel
        .append("text")
        .attr("y", height - room_for_axis + room_for_title)
        .attr("x", width / 2 - room_for_axis / 2)
        .style("fill", "white")
        .attr("font-size", "12px")
        .attr("text-anchor", "middle")
        .text(defaultOptiopns.xLabelName);
    }

    if (defaultOptiopns.yLabelName) {
      const yAxisLabel = svg
        .append("g")
        .attr("id", "yAxisLabel")
        .attr("transform", "translate(" + 35 + "," + 35 + ")");

      yAxisLabel
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -height / 2 + room_for_axis - room_for_title)
        .attr("y", 30)
        .attr("dy", "-4.7em")
        .style("fill", "white")
        .attr("font-size", "12px")
        .attr("text-anchor", "middle")
        .text(defaultOptiopns.yLabelName);
    }

    // Draw legend (static)
    defaultOptiopns?.thresholds?.forEach((element, index) => {
      const xPos =
        room_for_axis + (width / defaultOptiopns.thresholds.length) * index;
      legend
        .append("rect")
        .attr("x", xPos)
        .attr("y", height - room_for_legend)
        .attr("width", 16)
        .attr("height", 16)
        .attr("stroke", "black")
        .attr("fill", element.color);

      legend
        .append("text")
        .attr("x", xPos + 20)
        .attr("y", height - room_for_legend + 8)
        .attr("font-size", 14)
        .attr("alignment-baseline", "middle")
        .attr("fill", "white")
        .text(element.label);
    });

    // Draw thresholds
    defaultOptiopns?.thresholds?.forEach((element) => {
      thresholds
        .append("line")
        .style("stroke", element.color)
        .style("stroke-width", 2)
        .attr("x1", room_for_axis)
        .attr("y1", yScale(element.value))
        .attr("x2", width)
        .attr("y2", yScale(element.value));
    });

    graphVarsRef.current = [
      svg,
      dotsGroup,
      xScaleGroup,
      xGridsGroup,
      yScale,
      yScaleGroup,
      yGridsGroup,
      thresholds,
      legend,
    ];

    initialDataset?.forEach((sample) => {
      sample.interferenceFrequency&&dataRef.current.push([sample.timestamp, sample.interferenceFrequency]);
    });

    /** Interval updater */
    updateTimer.current = setInterval(() => {
      const now = new Date();
      const lookback = new Date(now.getTime() - lookback_s * 1000);

      // Clean old points
      dataRef.current = dataRef.current.filter((d) => d[0] > lookback);

      render(dataRef.current, now, lookback, graphVarsRef.current);
    }, updateIntervalMs);

    return () => clearInterval(updateTimer.current);
  }, []);

  useEffect(() => {
    freeze === true && clearInterval(updateTimer.current);
    const now = new Date();
    const lookback = new Date(now.getTime() - lookback_s * 1000);

    // Add interference events
    interferenceValuesRef.current.forEach((val) => {
      val&&dataRef.current.push([now, val]);
    });
    interferenceValuesRef.current = [];

    // Clean old points
    dataRef.current = dataRef.current.filter((d) => d[0] > lookback);

    render(dataRef.current, now, lookback, graphVarsRef.current);
  }, [freeze]);

  /** Render dynamic updates */
  const render = (data, now, lookback, graphVars) => {
    const [
      svg,
      dotsGroup,
      xScaleGroup,
      xGridsGroup,
      yScale,
      yScaleGroup,
      yGridsGroup,
    ] = graphVars;

    const xScale = d3
      .scaleUtc()
      .domain([lookback, now])
      .range([room_for_axis + 5, width]);

    const radius = width / 200;

    // JOIN
    const circles = dotsGroup.selectAll("circle").data(data);

    // EXIT
    circles.exit().remove();

    // UPDATE
    circles
      .attr("cx", (d) => xScale(d[0]))
      .attr("cy", (d) => yScale(d[1]))
      .attr("r", radius)
      .attr("fill", "red");

    // ENTER
    circles
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d[0]))
      .attr("cy", (d) => yScale(d[1]))
      .attr("r", radius)
      .attr("fill", "red");

    // Update axes
    const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat("%H:%M:%S"));
    xScaleGroup
      .attr(
        "transform",
        `translate(-5,${height - room_for_axis - room_for_legend + room_for_title})`,
      )
      .call(xAxis);

    const yAxis = d3.axisLeft(yScale);

    if (defaultOptiopns.yTicks) {
      const step =
        (defaultOptiopns.yMaxValue - defaultOptiopns.yMinValue) /
        defaultOptiopns.yTicks;
      yAxis.tickValues(
        d3.range(
          defaultOptiopns.yMinValue,
          defaultOptiopns.yMaxValue + step,
          step,
        ),
      );
    }
    yScaleGroup
      .attr("transform", `translate(${room_for_axis},${room_for_title})`)
      .call(yAxis);

    // Grids
    xGridsGroup
      .attr(
        "transform",
        `translate(-5,${height - room_for_axis - room_for_legend + room_for_title})`,
      )
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-height + room_for_axis + 10 + room_for_legend)
          .tickFormat(""),
      );

    yGridsGroup
      .attr("transform", `translate(${room_for_axis},${room_for_title})`)
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-width + room_for_axis)
          .tickFormat(""),
      );
  };

  return (
    <div
      className="bar-graph p-2"
      id="container"
      style={{ justifyContent: "center" }}
    >
      <svg
        ref={ref}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: "100%",
          // height: "auto",
          display: "block",
        }}
      ></svg>
    </div>
  );
};

export default RealtimeDotsGraph;
